import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, FilterIcon, PlusIcon, EditIcon, HistoryIcon, ToggleLeftIcon, ToggleRightIcon, DownloadIcon, UploadIcon, AlertTriangleIcon, XIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { StatusBadge } from '../../StatusBadge';
import { getCountryName } from '../../utils/countryUtils';
export function TaxCodesList({
  taxCodes,
  onAddNew,
  onEdit,
  onToggleStatus,
  onViewAuditHistory,
  onBulkDeactivate,
  locale = 'en-US'
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    country: '',
    taxType: '',
    isInclusive: '',
    isExempt: '',
    status: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [bulkActionMenuOpen, setBulkActionMenuOpen] = useState(false);
  const [countryGroupingMenuOpen, setCountryGroupingMenuOpen] = useState(false);
  const [showCountryGrouping, setShowCountryGrouping] = useState(false);
  // Refs for dropdown menus
  const bulkActionButtonRef = useRef(null);
  const bulkActionMenuRef = useRef(null);
  const countryGroupingButtonRef = useRef(null);
  const countryGroupingMenuRef = useRef(null);
  // Handle clicks outside the menus
  useEffect(() => {
    const handleClickOutside = event => {
      if (bulkActionMenuOpen && !bulkActionButtonRef.current?.contains(event.target) && !bulkActionMenuRef.current?.contains(event.target)) {
        setBulkActionMenuOpen(false);
      }
      if (countryGroupingMenuOpen && !countryGroupingButtonRef.current?.contains(event.target) && !countryGroupingMenuRef.current?.contains(event.target)) {
        setCountryGroupingMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [bulkActionMenuOpen, countryGroupingMenuOpen]);
  const handleFilterChange = (name, value) => {
    setFilters({
      ...filters,
      [name]: value
    });
  };
  const clearFilters = () => {
    setFilters({
      country: '',
      taxType: '',
      isInclusive: '',
      isExempt: '',
      status: ''
    });
    setSearchTerm('');
  };
  const filteredTaxCodes = taxCodes.filter(taxCode => {
    // Search term filtering
    if (searchTerm && !taxCode.code.toLowerCase().includes(searchTerm.toLowerCase()) && !taxCode.description.toLowerCase().includes(searchTerm.toLowerCase()) && !(taxCode.jurisdiction && taxCode.jurisdiction.toLowerCase().includes(searchTerm.toLowerCase()))) {
      return false;
    }
    // Country filtering
    if (filters.country && taxCode.country !== filters.country) {
      return false;
    }
    // Tax type filtering
    if (filters.taxType && taxCode.taxType !== filters.taxType) {
      return false;
    }
    // Inclusive filtering
    if (filters.isInclusive === 'yes' && !taxCode.isInclusive) {
      return false;
    }
    if (filters.isInclusive === 'no' && taxCode.isInclusive) {
      return false;
    }
    // Exempt filtering
    if (filters.isExempt === 'yes' && !taxCode.isExempt) {
      return false;
    }
    if (filters.isExempt === 'no' && taxCode.isExempt) {
      return false;
    }
    // Status filtering
    if (filters.status && taxCode.status.toLowerCase() !== filters.status.toLowerCase()) {
      return false;
    }
    return true;
  });
  // Get unique countries and tax types for filters
  const uniqueCountries = [...new Set(taxCodes.map(tc => tc.country))].sort();
  const uniqueTaxTypes = [...new Set(taxCodes.map(tc => tc.taxType))].sort();
  // Group tax codes by country for country grouping view
  const taxCodesByCountry = filteredTaxCodes.reduce((acc, taxCode) => {
    if (!acc[taxCode.country]) {
      acc[taxCode.country] = [];
    }
    acc[taxCode.country].push(taxCode);
    return acc;
  }, {});
  // Further group by jurisdiction within each country
  const taxCodesByCountryAndJurisdiction = Object.keys(taxCodesByCountry).reduce((acc, country) => {
    acc[country] = taxCodesByCountry[country].reduce((jurisdictions, taxCode) => {
      const jurisdiction = taxCode.jurisdiction || 'Default';
      if (!jurisdictions[jurisdiction]) {
        jurisdictions[jurisdiction] = [];
      }
      jurisdictions[jurisdiction].push(taxCode);
      return jurisdictions;
    }, {});
    return acc;
  }, {});
  const handleExportTaxCodes = () => {
    // In a real app, this would generate a CSV or JSON file
    console.log('Exporting tax codes:', filteredTaxCodes);
    alert('Tax codes exported successfully (simulated)');
  };
  const handleImportTaxCodes = () => {
    // In a real app, this would open a file dialog
    console.log('Importing tax codes');
    alert('Tax code import functionality would open a file dialog here');
  };
  const handleBulkDeactivateByCountry = country => {
    onBulkDeactivate({
      country
    });
    setBulkActionMenuOpen(false);
  };
  const handleBulkDeactivateByType = taxType => {
    onBulkDeactivate({
      taxType
    });
    setBulkActionMenuOpen(false);
  };
  return <div className="space-y-6" data-id="element-9813">
      {/* Filters and actions bar */}
      <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-9814">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-9815">
          <div className="flex-1 max-w-md" data-id="element-9816">
            <div className="relative rounded-md shadow-sm" data-id="element-9817">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9818">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-9819" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search tax codes..." data-id="element-9820" />
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-9821">
            <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9822">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-9823" />
              Filters
              {Object.values(filters).some(f => f !== '') && <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-9824">
                  {Object.values(filters).filter(f => f !== '').length}
                </span>}
            </button>
            {/* View toggle */}
            <div className="inline-flex shadow-sm rounded-md" data-id="element-9825">
              <button onClick={() => setViewMode('table')} className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-l-md ${viewMode === 'table' ? 'bg-indigo-100 text-indigo-800' : 'bg-white text-gray-700 hover:bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-9826">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-9827">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" data-id="element-9828" />
                </svg>
                Table
              </button>
              <button onClick={() => {
              setViewMode('country');
              setShowCountryGrouping(true);
            }} className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-r-md ${viewMode === 'country' ? 'bg-indigo-100 text-indigo-800' : 'bg-white text-gray-700 hover:bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-9829">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-9830">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" data-id="element-9831" />
                </svg>
                By Country
              </button>
            </div>
            {/* Bulk actions dropdown */}
            <div className="relative" data-id="element-9832">
              <button ref={bulkActionButtonRef} onClick={() => setBulkActionMenuOpen(!bulkActionMenuOpen)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9833">
                Bulk Actions
                <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-9834" />
              </button>
              {bulkActionMenuOpen && <div ref={bulkActionMenuRef} className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-9835">
                  <div className="py-1" role="menu" aria-orientation="vertical" data-id="element-9836">
                    <button onClick={handleImportTaxCodes} className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-9837">
                      <UploadIcon className="mr-2 h-4 w-4 text-gray-500" data-id="element-9838" />
                      Import Tax Codes
                    </button>
                    <button onClick={handleExportTaxCodes} className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-9839">
                      <DownloadIcon className="mr-2 h-4 w-4 text-gray-500" data-id="element-9840" />
                      Export Tax Codes
                    </button>
                    <div className="border-t border-gray-100 my-1" data-id="element-9841"></div>
                    <div className="px-4 py-1 text-xs font-semibold text-gray-500" data-id="element-9842">
                      Deactivate By Country
                    </div>
                    {uniqueCountries.map(country => <button key={country} onClick={() => handleBulkDeactivateByCountry(country)} className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-9843">
                        <AlertTriangleIcon className="mr-2 h-4 w-4 text-amber-500" data-id="element-9844" />
                        Deactivate all {getCountryName(country)} tax codes
                      </button>)}
                    <div className="border-t border-gray-100 my-1" data-id="element-9845"></div>
                    <div className="px-4 py-1 text-xs font-semibold text-gray-500" data-id="element-9846">
                      Deactivate By Type
                    </div>
                    {uniqueTaxTypes.map(taxType => <button key={taxType} onClick={() => handleBulkDeactivateByType(taxType)} className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-9847">
                        <AlertTriangleIcon className="mr-2 h-4 w-4 text-amber-500" data-id="element-9848" />
                        Deactivate all {taxType} tax codes
                      </button>)}
                  </div>
                </div>}
            </div>
            <button onClick={onAddNew} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9849">
              <PlusIcon className="h-4 w-4 mr-2" data-id="element-9850" />
              Add Tax Code
            </button>
          </div>
        </div>
        {/* Filter chips */}
        {Object.values(filters).some(f => f !== '') && <div className="mt-4 flex flex-wrap gap-2" data-id="element-9851">
            {filters.country && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-9852">
                Country: {getCountryName(filters.country)}
                <button type="button" onClick={() => handleFilterChange('country', '')} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none" data-id="element-9853">
                  <XIcon className="h-3 w-3" data-id="element-9854" />
                </button>
              </span>}
            {filters.taxType && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800" data-id="element-9855">
                Type: {filters.taxType}
                <button type="button" onClick={() => handleFilterChange('taxType', '')} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-purple-400 hover:bg-purple-200 hover:text-purple-500 focus:outline-none" data-id="element-9856">
                  <XIcon className="h-3 w-3" data-id="element-9857" />
                </button>
              </span>}
            {filters.isInclusive && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-9858">
                Inclusive: {filters.isInclusive}
                <button type="button" onClick={() => handleFilterChange('isInclusive', '')} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none" data-id="element-9859">
                  <XIcon className="h-3 w-3" data-id="element-9860" />
                </button>
              </span>}
            {filters.isExempt && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800" data-id="element-9861">
                Exempt: {filters.isExempt}
                <button type="button" onClick={() => handleFilterChange('isExempt', '')} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-yellow-400 hover:bg-yellow-200 hover:text-yellow-500 focus:outline-none" data-id="element-9862">
                  <XIcon className="h-3 w-3" data-id="element-9863" />
                </button>
              </span>}
            {filters.status && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800" data-id="element-9864">
                Status: {filters.status}
                <button type="button" onClick={() => handleFilterChange('status', '')} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-500 focus:outline-none" data-id="element-9865">
                  <XIcon className="h-3 w-3" data-id="element-9866" />
                </button>
              </span>}
            <button type="button" onClick={clearFilters} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9867">
              Clear All
            </button>
          </div>}
        {/* Expanded filters */}
        {showFilters && <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-9868">
            <div className="sm:col-span-2" data-id="element-9869">
              <label htmlFor="country-filter" className="block text-sm font-medium text-gray-700" data-id="element-9870">
                Country
              </label>
              <select id="country-filter" value={filters.country} onChange={e => handleFilterChange('country', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-9871">
                <option value="" data-id="element-9872">All Countries</option>
                {uniqueCountries.map(country => <option key={country} value={country} data-id="element-9873">
                    {getCountryName(country)}
                  </option>)}
              </select>
            </div>
            <div className="sm:col-span-2" data-id="element-9874">
              <label htmlFor="tax-type-filter" className="block text-sm font-medium text-gray-700" data-id="element-9875">
                Tax Type
              </label>
              <select id="tax-type-filter" value={filters.taxType} onChange={e => handleFilterChange('taxType', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-9876">
                <option value="" data-id="element-9877">All Types</option>
                {uniqueTaxTypes.map(type => <option key={type} value={type} data-id="element-9878">
                    {type}
                  </option>)}
              </select>
            </div>
            <div className="sm:col-span-2" data-id="element-9879">
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700" data-id="element-9880">
                Status
              </label>
              <select id="status-filter" value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-9881">
                <option value="" data-id="element-9882">All Statuses</option>
                <option value="Active" data-id="element-9883">Active</option>
                <option value="Inactive" data-id="element-9884">Inactive</option>
              </select>
            </div>
            <div className="sm:col-span-2" data-id="element-9885">
              <label htmlFor="inclusive-filter" className="block text-sm font-medium text-gray-700" data-id="element-9886">
                Inclusive
              </label>
              <select id="inclusive-filter" value={filters.isInclusive} onChange={e => handleFilterChange('isInclusive', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-9887">
                <option value="" data-id="element-9888">All</option>
                <option value="yes" data-id="element-9889">Yes</option>
                <option value="no" data-id="element-9890">No</option>
              </select>
            </div>
            <div className="sm:col-span-2" data-id="element-9891">
              <label htmlFor="exempt-filter" className="block text-sm font-medium text-gray-700" data-id="element-9892">
                Exempt
              </label>
              <select id="exempt-filter" value={filters.isExempt} onChange={e => handleFilterChange('isExempt', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-9893">
                <option value="" data-id="element-9894">All</option>
                <option value="yes" data-id="element-9895">Yes</option>
                <option value="no" data-id="element-9896">No</option>
              </select>
            </div>
          </div>}
      </div>
      {/* Table View */}
      {viewMode === 'table' && <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-9897">
          <div className="overflow-x-auto" data-id="element-9898">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-9899">
              <thead className="bg-gray-50" data-id="element-9900">
                <tr data-id="element-9901">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9902">
                    Code
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9903">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9904">
                    Rate
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9905">
                    Inclusive
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9906">
                    Exempt
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9907">
                    Country
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9908">
                    Tax Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9909">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9910">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-9911">
                {filteredTaxCodes.length === 0 ? <tr data-id="element-9912">
                    <td colSpan={9} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-9913">
                      No tax codes found matching your criteria.
                    </td>
                  </tr> : filteredTaxCodes.map(taxCode => <tr key={taxCode.id} className="hover:bg-gray-50" data-id="element-9914">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-9915">
                        {taxCode.code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-9916">
                        {taxCode.description}
                        {taxCode.jurisdiction && <span className="ml-1 text-xs text-gray-400" data-id="element-9917">
                            ({taxCode.jurisdiction})
                          </span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-9918">
                        {taxCode.isExempt ? 'Exempt' : `${(taxCode.rate * 100).toFixed(2)}%`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-9919">
                        {taxCode.isInclusive ? <CheckIcon className="h-5 w-5 text-green-500" data-id="element-9920" /> : <XIcon className="h-5 w-5 text-gray-400" data-id="element-9921" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-9922">
                        {taxCode.isExempt ? <CheckIcon className="h-5 w-5 text-green-500" data-id="element-9923" /> : <XIcon className="h-5 w-5 text-gray-400" data-id="element-9924" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-9925">
                        {getCountryName(taxCode.country)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-9926">
                        {taxCode.taxType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-9927">
                        <StatusBadge status={taxCode.status === 'Active' ? 'success' : 'error'} text={taxCode.status} data-id="element-9928" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-9929">
                        <div className="flex justify-end space-x-2" data-id="element-9930">
                          <button onClick={() => onEdit(taxCode)} className="text-indigo-600 hover:text-indigo-900" data-id="element-9931">
                            <EditIcon className="h-4 w-4" data-id="element-9932" />
                          </button>
                          <button onClick={() => onToggleStatus(taxCode.id)} className={`${taxCode.status === 'Active' ? 'text-amber-600 hover:text-amber-900' : 'text-green-600 hover:text-green-900'}`} title={taxCode.status === 'Active' ? 'Deactivate' : 'Activate'} data-id="element-9933">
                            {taxCode.status === 'Active' ? <ToggleRightIcon className="h-4 w-4" data-id="element-9934" /> : <ToggleLeftIcon className="h-4 w-4" data-id="element-9935" />}
                          </button>
                          <button onClick={() => onViewAuditHistory(taxCode)} className="text-gray-600 hover:text-gray-900" data-id="element-9936">
                            <HistoryIcon className="h-4 w-4" data-id="element-9937" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
      {/* Country Grouping View */}
      {viewMode === 'country' && showCountryGrouping && <div className="space-y-6" data-id="element-9938">
          {Object.keys(taxCodesByCountryAndJurisdiction).length === 0 ? <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center" data-id="element-9939">
              <p className="text-gray-500" data-id="element-9940">
                No tax codes found matching your criteria.
              </p>
            </div> : Object.keys(taxCodesByCountryAndJurisdiction).sort().map(country => <div key={country} className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-9941">
                  <div className="px-4 py-5 border-b border-gray-200 sm:px-6 bg-gray-50" data-id="element-9942">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9943">
                      {getCountryName(country)} ({country})
                    </h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-9944">
                      {Object.values(taxCodesByCountryAndJurisdiction[country]).flat().filter(tc => tc.status === 'Active').length}{' '}
                      active tax codes
                    </p>
                  </div>
                  <div className="px-4 py-5 sm:p-6" data-id="element-9945">
                    {Object.keys(taxCodesByCountryAndJurisdiction[country]).sort().map(jurisdiction => <div key={jurisdiction} className="mb-6 last:mb-0" data-id="element-9946">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3" data-id="element-9947">
                            {jurisdiction}
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-9948">
                            {taxCodesByCountryAndJurisdiction[country][jurisdiction].map(taxCode => <div key={taxCode.id} className={`border rounded-lg p-4 ${taxCode.status === 'Active' ? 'border-gray-200' : 'border-gray-200 bg-gray-50'}`} data-id="element-9949">
                                <div className="flex justify-between items-start" data-id="element-9950">
                                  <div data-id="element-9951">
                                    <h5 className="font-medium text-indigo-600" data-id="element-9952">
                                      {taxCode.code}
                                    </h5>
                                    <p className="text-sm text-gray-600" data-id="element-9953">
                                      {taxCode.description}
                                    </p>
                                  </div>
                                  <StatusBadge status={taxCode.status === 'Active' ? 'success' : 'error'} text={taxCode.status} size="sm" data-id="element-9954" />
                                </div>
                                <div className="mt-2 text-sm text-gray-500" data-id="element-9955">
                                  <div className="grid grid-cols-2 gap-x-2 gap-y-1" data-id="element-9956">
                                    <div data-id="element-9957">Rate:</div>
                                    <div className="font-medium" data-id="element-9958">
                                      {taxCode.isExempt ? 'Exempt' : `${(taxCode.rate * 100).toFixed(2)}%`}
                                    </div>
                                    <div data-id="element-9959">Type:</div>
                                    <div className="font-medium" data-id="element-9960">
                                      {taxCode.taxType}
                                    </div>
                                    <div data-id="element-9961">Inclusive:</div>
                                    <div className="font-medium" data-id="element-9962">
                                      {taxCode.isInclusive ? 'Yes' : 'No'}
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-4 flex justify-end space-x-2" data-id="element-9963">
                                  <button onClick={() => onEdit(taxCode)} className="text-indigo-600 hover:text-indigo-900" data-id="element-9964">
                                    <EditIcon className="h-4 w-4" data-id="element-9965" />
                                  </button>
                                  <button onClick={() => onToggleStatus(taxCode.id)} className={`${taxCode.status === 'Active' ? 'text-amber-600 hover:text-amber-900' : 'text-green-600 hover:text-green-900'}`} title={taxCode.status === 'Active' ? 'Deactivate' : 'Activate'} data-id="element-9966">
                                    {taxCode.status === 'Active' ? <ToggleRightIcon className="h-4 w-4" data-id="element-9967" /> : <ToggleLeftIcon className="h-4 w-4" data-id="element-9968" />}
                                  </button>
                                  <button onClick={() => onViewAuditHistory(taxCode)} className="text-gray-600 hover:text-gray-900" data-id="element-9969">
                                    <HistoryIcon className="h-4 w-4" data-id="element-9970" />
                                  </button>
                                </div>
                              </div>)}
                          </div>
                        </div>)}
                  </div>
                </div>)}
        </div>}
    </div>;
}